/*
 * AdminCodigoEticaTab — CRUD administrativo para a biblioteca de
 * Código de Ética e Conduta. Cada PDF vira um item independente que os
 * funcionários acessam separadamente no portal.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Upload, FileText, Eye, Trash2, Loader2, Search, ShieldCheck,
  Edit3, ToggleLeft, ToggleRight, HardDrive, Send, PauseCircle, PlayCircle,
} from "lucide-react";
import AdminSendReceiptDialog from "./AdminSendReceiptDialog";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function UploadDialog({ open, onClose, onCreated }) {
  const { token } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(""); setDescription(""); setFile(null); setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const submit = async () => {
    if (!title.trim()) return toast.error("Informe o título.");
    if (!file) return toast.error("Selecione um PDF.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      await axios.post(`${API}/admin/codigo-etica/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`,
                   "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Documento publicado com sucesso.");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="codigo-etica-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <ShieldCheck className="w-5 h-5" />
            Novo documento — Código de Ética
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">
              Título *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Código de Conduta Corporativo — 2026"
              data-testid="codigo-etica-title"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">
              Descrição (opcional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Versão vigente com atualizações de 2026 sobre assédio e integridade."
              rows={3}
              data-testid="codigo-etica-description"
              className="mt-1 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">
              PDF (máx. 20 MB) *
            </label>
            <Input
              ref={fileRef} type="file" accept=".pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="codigo-etica-file"
              className="mt-1 text-xs"
            />
            {file && (
              <p className="text-[11px] text-slate-500 mt-1">
                {file.name} — {fmtSize(file.size)}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !title.trim() || !file}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="codigo-etica-submit"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publicando…</>
              : <><Upload className="w-4 h-4 mr-2" />Publicar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ open, item, onClose, onSaved }) {
  const { token } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setTitle(item.title || "");
      setDescription(item.description || "");
      setActive(item.active !== false);
    }
  }, [item]);

  if (!item) return null;

  const save = async () => {
    if (!title.trim()) return toast.error("Título obrigatório.");
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/codigo-etica/${item.id}`,
        { title: title.trim(), description: description.trim(), active },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 });
      toast.success("Documento atualizado.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="codigo-etica-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <Edit3 className="w-5 h-5" />
            Editar documento
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Título</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Descrição</label>
            <Textarea value={description}
                       onChange={(e) => setDescription(e.target.value)}
                       rows={3} className="mt-1 text-sm" />
          </div>
          <button type="button"
                   onClick={() => setActive(!active)}
                   className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition"
                   data-testid="codigo-etica-toggle-active">
            {active
              ? <ToggleRight className="w-6 h-6 text-emerald-600" />
              : <ToggleLeft className="w-6 h-6 text-slate-400" />}
            <span>
              {active
                ? "Visível para os funcionários"
                : "Oculto para os funcionários"}
            </span>
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={busy || !title.trim()}
                   className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando…</>
              : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCodigoEticaTab() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [totalBytes, setTotalBytes] = useState(0);
  const [sending, setSending] = useState(null); // item being dispatched
  const [suspending, setSuspending] = useState(null); // id being toggled

  const toggleSuspension = async (it) => {
    const target = !it.signatures_suspended;
    setSuspending(it.id);
    try {
      await axios.patch(
        `${API}/admin/codigo-etica/${it.id}`,
        { signatures_suspended: target },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(target
        ? "Assinaturas SUSPENSAS — funcionários não serão mais bloqueados."
        : "Assinaturas RETOMADAS — funcionários voltarão a ser notificados.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar");
    } finally { setSuspending(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/codigo-etica/list`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 20000,
      });
      setItems(r.data?.items || []);
      setTotalBytes(r.data?.total_bytes || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail
                    || "Falha ao carregar documentos.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const view = async (item) => {
    try {
      const r = await axios.get(
        `${API}/admin/codigo-etica/${item.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", timeout: 60000 },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Falha ao abrir.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Excluir "${item.title}"?`)) return;
    setDeleting(item.id);
    try {
      await axios.delete(`${API}/admin/codigo-etica/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 20000,
      });
      toast.success("Documento excluído.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    } finally { setDeleting(null); }
  };

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.title || "").toLowerCase().includes(q)
        || (it.description || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <UploadDialog open={uploadOpen}
                     onClose={() => setUploadOpen(false)}
                     onCreated={load} />
      <EditDialog open={!!editing} item={editing}
                   onClose={() => setEditing(null)} onSaved={load} />

      {/* Header + upload */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Código de Ética e Conduta
              <Badge variant="outline" className="text-[10px] font-mono">
                {items.length} documento(s)
              </Badge>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Publique os PDFs institucionais. Cada documento fica
              acessível separadamente pelos funcionários no portal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-50 rounded px-2.5 py-1.5 border border-slate-200">
              <HardDrive className="w-3.5 h-3.5" />
              {fmtSize(totalBytes)} armazenado
            </div>
            <Button
              onClick={() => setUploadOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="codigo-etica-open-upload"
            >
              <Upload className="w-4 h-4 mr-2" />
              Novo documento
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por título ou descrição…"
          className="pl-9"
          data-testid="codigo-etica-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-14 text-center">
            <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">
              {items.length === 0
                ? "Nenhum documento publicado ainda."
                : "Nenhum documento corresponde à busca."}
            </p>
            {items.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">
                Clique em "Novo documento" para publicar o primeiro PDF.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="codigo-etica-list">
          {filtered.map((it) => (
            <Card
              key={it.id}
              className={`border-slate-200 transition ${
                it.active === false ? "bg-slate-50 opacity-70" : "bg-white"
              }`}
              data-testid={`codigo-etica-item-${it.id}`}
            >
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-800 truncate">
                      {it.title}
                    </span>
                    {it.active === false && (
                      <Badge variant="outline"
                              className="border-slate-300 text-slate-500 text-[10px]">
                        Oculto
                      </Badge>
                    )}
                    {it.signatures_suspended && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold">
                        <PauseCircle className="w-3 h-3 mr-0.5" />
                        Assinaturas suspensas
                      </Badge>
                    )}
                    <Badge variant="outline"
                            className="border-slate-200 text-slate-500 text-[10px] font-mono">
                      {fmtSize(it.size_bytes)}
                    </Badge>
                    {it.views_count > 0 && (
                      <Badge variant="outline"
                              className="border-sky-200 text-sky-700 text-[10px]">
                        {it.views_count} visualização(ões)
                      </Badge>
                    )}
                  </div>
                  {it.description && (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                      {it.description}
                    </p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1 font-mono">
                    Publicado em {fmtDate(it.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline"
                          onClick={() => view(it)}
                          data-testid={`codigo-etica-view-${it.id}`}
                          className="h-8">
                    <Eye className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Ver</span>
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => setEditing(it)}
                          data-testid={`codigo-etica-edit-${it.id}`}
                          className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                    <Edit3 className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Editar</span>
                  </Button>
                  <Button size="sm"
                          onClick={() => setSending(it)}
                          data-testid={`codigo-etica-send-${it.id}`}
                          className="h-8 bg-sky-600 hover:bg-sky-700 text-white">
                    <Send className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Enviar</span>
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => toggleSuspension(it)}
                          disabled={suspending === it.id}
                          data-testid={`codigo-etica-suspend-${it.id}`}
                          className={`h-8 ${
                            it.signatures_suspended
                              ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              : "border-amber-300 text-amber-700 hover:bg-amber-50"
                          }`}
                          title={it.signatures_suspended
                            ? "Retomar exigência de assinatura"
                            : "Suspender exigência de assinatura"}>
                    {suspending === it.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : it.signatures_suspended ? (
                      <><PlayCircle className="w-3.5 h-3.5 sm:mr-1" />
                        <span className="hidden sm:inline">Retomar</span></>
                    ) : (
                      <><PauseCircle className="w-3.5 h-3.5 sm:mr-1" />
                        <span className="hidden sm:inline">Suspender</span></>
                    )}
                  </Button>
                  <Button size="sm" variant="outline"
                          onClick={() => remove(it)}
                          disabled={deleting === it.id}
                          data-testid={`codigo-etica-delete-${it.id}`}
                          className="h-8 border-red-200 text-red-600 hover:bg-red-50">
                    {deleting === it.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AdminSendReceiptDialog
        open={!!sending}
        onOpenChange={(v) => { if (!v) setSending(null); }}
        sourceKind="codigo_etica"
        sourceId={sending?.id}
        documentTitle={sending?.title}
      />
    </div>
  );
}
